import React, { SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import ConsumptionElementSlide from '../slides/ConsumptionElementSlide'
import HeatingTypeSlide from '../slides/HeatingTypeSlide'
import ObjectTypeSlide from '../slides/ObjectTypeSlide'
import PersonCountSlide from '../slides/PersonCountSlide'
import SecondaryHeatingSlide from '../slides/SecondaryHeatingSlide'
import { HeatingSlideStateType } from '../types/types'

interface Props {
	options: ObjectInformation
	setOptions: React.Dispatch<React.SetStateAction<ObjectInformation>>
	initialState: any
	saveButton: (disabled: boolean) => React.JSX.Element
}

export interface ObjectInformation {
	type: string | null
	heatingType: string | null
	squares: number | null
	personCount: number | null
	heatPump: boolean | null
	underfloorHeating: boolean | null
	fireplace: boolean | null
	consumptionElements: any[]
}

const isEqualObjects = (a1: any[], a2: any[]) => {
	if (a1.length !== a2.length) {
		return false
	}

	a1.sort()
	a2.sort()

	for (let i = 0; i < a1.length; i++) {
		// Compare each object in the arrays
		if (!(JSON.stringify(a1[i]) === JSON.stringify(a2[i]))) {
			return false
		}
	}

	return true
}

const ObjectBasicQuestions: React.FC<Props> = ({
	options,
	setOptions,
	initialState,
	saveButton,
}) => {
	const { t } = useTranslation('pages_onboarding')

	const secondaryHeatingOptions = [
		{
			label: t('objectBasicQuestions.ilmalampopumppu'),
			value: 'heatPump',
		},
		{
			label: t('objectBasicQuestions.lattialammitys'),
			value: 'underfloorHeating',
		},
		{
			label: t('objectBasicQuestions.takka'),
			value: 'fireplace',
		},
	]

	return (
		<>
			<div className='question-container'>
				<div className='w-[30%] min-w-[30%]'>
					<h4 className='text-text-dark dark:text-white'>{t('asuntotyyppi')}</h4>
				</div>
				<div className='question-content'>
					<ObjectTypeSlide
						state={[
							options.type,
							(value: SetStateAction<string | null>) => {
								setOptions(state => ({
									...state,
									type: typeof value === 'function' ? value(options.type) : value,
								}))
							},
						]}
						onClick={value => {
							setOptions(state => ({
								...state,
								type: value,
							}))
						}}
					/>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						{saveButton(options.type === initialState.type)}
					</div>
				</div>
			</div>

			<div className='question-container'>
				<div className='w-[30%] min-w-[30%]'>
					<h4 className='text-text-dark dark:text-white'>
						{t('personCountSlide.montakoHenkiloa')}
					</h4>
				</div>
				<div className='question-content'>
					<PersonCountSlide
						onInteraction={() => ({})}
						state={[
							options.personCount ?? 0,
							(value: React.SetStateAction<number>) => {
								setOptions(state => ({
									...state,
									personCount: typeof value === 'function' ? value(state.personCount ?? 0) : value,
								}))
							},
						]}
						onClick={value => {
							setOptions(state => ({
								...state,
								personCount: value,
							}))
						}}
					/>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						{saveButton(options.personCount === initialState.personCount)}
					</div>
				</div>
			</div>

			<div className='question-container'>
				<div className='w-[30%] min-w-[30%]'>
					<h4 className='text-text-dark dark:text-white'>
						{t('objectBasicQuestions.ensisijainenLammitys')}
					</h4>
				</div>
				<div className='question-content'>
					<HeatingTypeSlide
						state={[
							{
								selected: options.heatingType || '',
								squares: options.squares?.toString() || '',
							},
							(value: React.SetStateAction<HeatingSlideStateType>) => {
								setOptions(state => {
									const newValue =
										typeof value === 'function'
											? value({
													selected: state.heatingType || '',
													squares: state.squares?.toString() || '',
												})
											: value
									return {
										...state,
										heatingType: newValue.selected,
										squares: newValue.squares ? parseFloat(newValue.squares) : null,
									}
								})
							},
						]}
						onInteraction={() => ({})}
					/>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						{saveButton(
							!(
								options.heatingType !== initialState.heatingType ||
								((options.heatingType?.includes('lectric') ?? false) &&
									options.squares !== initialState.squares)
							)
						)}
					</div>
				</div>
			</div>

			<div className='question-container'>
				<div className='w-[30%] min-w-[30%]'>
					<h4 className='text-text-dark dark:text-white'>
						{t('objectBasicQuestions.muitaLammitystapoja')}
					</h4>
				</div>
				<div className='question-content'>
					<SecondaryHeatingSlide
						state={[
							[
								...(options.heatPump ? ['heatPump'] : []),
								...(options.underfloorHeating ? ['underfloorHeating'] : []),
								...(options.fireplace ? ['fireplace'] : []),
							],
							(value: React.SetStateAction<string[]>) => {
								setOptions(state => {
									const newValue =
										typeof value === 'function'
											? value([
													...(options.heatPump ? ['heatPump'] : []),
													...(options.underfloorHeating ? ['underfloorHeating'] : []),
													...(options.fireplace ? ['fireplace'] : []),
												])
											: value

									return {
										...state,
										heatPump: newValue.includes('heatPump'),
										underfloorHeating: newValue.includes('underfloorHeating'),
										fireplace: newValue.includes('fireplace'),
									}
								})
							},
						]}
						onClick={() => ({})}
					/>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						{saveButton(
							secondaryHeatingOptions
								.map(e => {
									return initialState[e.value] === options[e.value as keyof ObjectInformation]
								})
								.every(isEqual => isEqual)
						)}
					</div>
				</div>
			</div>

			<div className='question-container'>
				<div className='w-[30%] min-w-[30%]'>
					<h4 className='text-text-dark dark:text-white'>{t('objectBasicQuestions.laitteet')}</h4>
				</div>
				<div className='question-content'>
					<ConsumptionElementSlide
						state={[
							new Set(options.consumptionElements || []),
							(value: React.SetStateAction<Set<any>>) => {
								setOptions(state => {
									const newValue =
										typeof value === 'function'
											? value(new Set(state.consumptionElements || []))
											: value
									return {
										...state,
										consumptionElements: Array.from(newValue),
									}
								})
							},
						]}
						saveButton={false}
					/>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						{saveButton(
							isEqualObjects(initialState.consumptionElements, options.consumptionElements)
						)}
					</div>
				</div>
			</div>
		</>
	)
}
export default ObjectBasicQuestions
